import React, { useMemo, useState } from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { useOpportunitiesInfinite, useCategories, useDestinations } from './hooks/useContentMarketingQueries';
import ContentCard from './components/ContentCard';
import { FilterChipGroup, IFilterChipOption } from 'ui/FilterChip';
import { Multiselect } from 'ui/Multiselect';
import { LoadingBar } from 'ui/NetworkStatusBar';
import { ESortOrder } from 'store/common/types';
import { SvgIcon } from 'ui/SvgIcon';
import LoaderIcon from 'ui/Icons/tabler/loader.component.svg';
import { IMarketingManagerOpportunityResponse } from 'services/MarketingManagerApi';
import { useDynamicParameters } from 'context/DynamicParameters/DynamicParametersContext';

type TSortSelection = 'newest' | 'exclusive';

export const ContentShowCards: React.FC = () => {
  const [selectedDestinations, setSelectedDestinations] = useState<string[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [sortSelection, setSortSelection] = useState<TSortSelection>('newest');

  const { dynamicParameters } = useDynamicParameters();

  // Map sort selection to API parameters
  const sortBy = sortSelection === 'newest' ? 'creationDate' : 'isExclusive';
  const order = ESortOrder.DESC;

  const { data, hasNextPage, isFetchingNextPage, fetchNextPage, status, isFetching } = useOpportunitiesInfinite({
    pageSize: 6,
    categoryUuids: selectedCategories,
    destinations: selectedDestinations,
    sortBy,
    order,
  });
  const cards = useMemo(() => (data ? data.pages.flatMap(p => p.data.items) : []), [data]);

  // Load categories
  const { data: categoriesData, isPending: categoriesPending } = useCategories();
  const categoryOptions: IFilterChipOption[] = useMemo(
    () =>
      categoriesData
        ? categoriesData.map(category => ({
            value: category.uuid,
            label: category.value,
          }))
        : [],
    [categoriesData]
  );

  // Load destinations
  const { data: destinationsData, isPending: destinationsPending } = useDestinations();
  const destinationOptions: IFilterChipOption[] = useMemo(
    () =>
      destinationsData
        ? destinationsData.map(destination => ({
            value: destination.name,
            label: destination.name,
          }))
        : [],
    [destinationsData]
  );

  // Sort dropdown options
  const sortOptions = [
    { value: 'newest', label: 'Newest first' },
    { value: 'exclusive', label: 'Exclusive' },
  ];

  const handleViewDetails = (item: IMarketingManagerOpportunityResponse) => {
    // Track click in Woopra
    if (dynamicParameters.WOOPRA_DOMAIN) {
      // @ts-ignore
      window.woopra &&
        // @ts-ignore
        window.woopra.track('content-card-click', {
          title: item.title,
          isExclusive: item.isExclusive,
          currency: item.currency,
          fullPrice: item.fullPrice,
          discountedPrice: item.discountedPrice,
          discountPercent: item.discountPercent,
        });
    }

    // Navigate to content detail page
    window.open(`/content/${item.uuid}`, '_blank');
  };

  // Determine loading states
  const filtersLoading = categoriesPending || destinationsPending;
  const contentLoading = isFetching;

  // Check if this is initial load (no filters applied)
  const isInitialLoad = selectedDestinations.length === 0 && selectedCategories.length === 0;

  return (
    <div className="container mx-auto max-w-1280px p-0">
      {/* Only show title and filters if filters are applied OR initial load has cards */}
      {(!isInitialLoad || (status === 'success' && cards.length > 0)) && (
        <>
          <div className="text-center mt-[40px] mb-[30px]">
            <h2 className="font-noe-display font-normal text-[36px] leading-[32px] text-black m-0">
              Latest Offers and News
            </h2>
            <div className="font-noe-display text-black text-[26px] mt-[15px]">
              See our Latest Offers, Last Minute Cancellations and News
            </div>
          </div>

          {/* Filters Section */}
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-[40px] w-full mb-[30px] px-[20px] crm-desktop:px-0">
            {/* Left column: destination and category filters */}
            <div className="flex flex-col gap-[20px]">
              <FilterChipGroup
                label="DESTINATION"
                options={destinationOptions}
                selectedValues={selectedDestinations}
                onChange={setSelectedDestinations}
              />

              <FilterChipGroup
                label="CATEGORIES"
                options={categoryOptions}
                selectedValues={selectedCategories}
                onChange={setSelectedCategories}
              />
            </div>

            {/* Right column: sort dropdown */}
            <div className="flex flex-col justify-end">
              <span className="block font-hurme-geometric-sans text-[12px] leading-[1.272] uppercase text-gray-100 tracking-wider mb-[10px]">
                SORT BY
              </span>
              <Multiselect
                options={sortOptions}
                selectedValues={[sortSelection]}
                onUpdate={(vals: string[]) => {
                  // Only update if a value is selected (prevent deselection)
                  if (vals.length > 0) {
                    setSortSelection(vals[0] as TSortSelection);
                  }
                }}
                isSingleSelectMode={true}
                isCloseOnSelect={true}
                hideCheckboxes={true}
                className="bg-ivory min-w-[250px]"
                itemsClassname="bg-ivory"
                itemCtaClassName="hover:bg-gray-10"
                placeholderText="Select sort"
              />
            </div>
          </div>
        </>
      )}

      {filtersLoading ? (
        <div className="flex justify-center my-[40px]">
          <LoadingBar label="Loading opportunities" />
        </div>
      ) : contentLoading ? (
        <div className="flex justify-center my-[40px]">
          <LoadingBar label="Loading opportunities" />
        </div>
      ) : (
        <>
          <div
            className={classNames('grid crm-desktop:px-0', {
              'grid-cols-1 w-[33%] mx-auto': cards.length === 1,
              'grid-cols-2 w-[66%] gap-[30px] mx-auto': cards.length === 2,
              'grid-cols-3 gap-[30px] px-[20px]': cards.length > 2,
            })}
          >
            {cards.map(item => (
              <ContentCard key={item.uuid} item={item} onViewDetails={() => handleViewDetails(item)} />
            ))}
          </div>

          {cards.length === 0 && status === 'success' && !isInitialLoad && (
            <div className="text-center text-gray-120 my-[40px]">No items to display</div>
          )}
        </>
      )}

      {hasNextPage && (
        <div className="flex justify-center mt-[20px]">
          <button
            className="bg-white mt-20px self-center block border border-brown-prime font-hurmegeometric-sans uppercase px-10px py-[9px] text-brown-prime text-sm items-center space-x-5px cursor-pointer min-w-[125px]"
            disabled={isFetchingNextPage}
            onClick={() => fetchNextPage()}
          >
            {isFetchingNextPage ? <SvgIcon IconComponent={LoaderIcon} className="w-4 h-4 fill-transparent stroke-brown-140 stroke-2 animate-spin" /> : 'Load More'}
          </button>
        </div>
      )}
    </div>
  );
};
